import React from "react";
import { Container, Row, Col } from "react-bootstrap";
import Image from "react-bootstrap/Image";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "motion/react";
import { ArrowRight, Shield, Zap, Code2, Cloud, Brain, Network, Star, CircleDot, Link2, FileText, Smartphone, Mail, Quote, ChevronLeft, ChevronRight } from "lucide-react";

import LayerTwo from "../assets/images/layers/Layer_2.png";
import flutterHero from "../assets/images/engineeing.png";
import lynksenseImg from "../assets/images/lynksense-dash.png";
import thinkcraftImg from "../assets/images/thinkcraft.png";
import middlewareImg from "../assets/images/middleware.png";
import flutterImg from "../assets/images/flutter.png";
import aiCopilotImg from "../assets/images/aiCopilot.png";
import aiLabImg from "../assets/images/aiLab.png";
import kleanmailImg from "../assets/images/kleanmail.png";

const EngineeringServices = () => {
  const approachCards = [
    {
      badge: "Development Philosophy",
      title: "AI-powered engineering, built for real-world impact.",
      text: "We design intelligent, practical solutions that align technology with business outcomes, ensuring every build delivers measurable value.",
    },
    {
      badge: "Capability & Execution",
      title: "End-to-end engineering, executed with precision.",
      text: "From architecture to deployment, we deliver scalable, high-quality solutions with strong accountability across the entire lifecycle.",
    },
    {
      badge: "Product & Solution Mindset",
      title: "Turning complexity into intuitive, outcome-driven systems.",
      text: "We build user-centric products that simplify complexity and create seamless, impactful digital experiences.",
    },
    {
      badge: "Experience-LED Delivery",
      title: "AI-accelerated delivery, backed by deep expertise.",
      text: "Combining domain experience with AI, we ensure reliable, scalable, and enterprise-grade engineering outcomes.",
    },
  ];
  const excellenceFeatures = [
    {
      icon: Code2,
      title: "Product & Platform Engineering",
      sub: "Design and build resilient digital products and enterprise platforms.",
      chips: [
        "Java",
        "Node.js",
        "Angular",
        "React",
        "HTML/CSS",
        "SQL Database",
        "MongoDB",
        "TypeScript",
        "Vue.js",
        "DevOps",
        "Next.js",
        "Prisma",
        ".NET",
        "C#",
        "Xamarin",
        "WinForms",
        "AWS",
        "NativeScript",
        "HubSpot",
        "PrimeFaces",
        "Joget",
        "PHP",
        "Python",
        "Android",
      ],
      metrics: ["150+ Projects Delivered", "10+ AI Projects Delivered"],
    },
    {
      icon: Cloud,
      title: "Cloud, DevOps & Scalability Engineering",
      sub: "Auto-scaling, high-availability infrastructure with observability.",
      chips: ["AWS", "Kubernetes", "Docker", "Terraform", "Jenkins"],
      metrics: ["20–35% cost optimization", "Handles loads with zero downtime"],
    },
    {
      icon: Brain,
      title: "Data, AI & Intelligent Systems",
      sub: "Secure data platforms, ML solutions, and GenAI automation.",
      chips: ["Python", "TensorFlow", "PyTorch", "OpenAI", "LangChain"],
      metrics: [
        "Data Accuracy: 99%+ data quality scores",
        "30–50% reduction in manual effort",
      ],
    },
    {
      icon: Network,
      title: "Enterprise Integration & Modernization",
      sub: "APIs, middleware, and legacy system transformation.",
      chips: ["REST API", "Kafka", "MuleSoft", "Redis", "PostgreSQL"],
      metrics: [
        "Weeks to Integrate New Systems",
        "Cloud & On-Prem Interoperability",
      ],
    },
  ];
  const solutions = [
    {
      icon: Link2,
      name: "Lynksense",
      tagline: "URL Shortening & Link Management Platform",
      description:
        "Secure and scalable platform to create, manage, and track short URLs with high performance and reliability. Designed for marketing, analytics, and internal tooling use cases.",
      image: lynksenseImg,
      link: "https://app.trym.ly/",
      features: [
        "Fast and reliable URL shortening",
        "Secure link generation and redirection",
        "Click tracking and analytics",
        "Scalable architecture for high traffic",
        "API support for system integration",
      ],
    },
    {
      icon: FileText,
      name: "ThinkCraft",
      tagline: "AI-Powered BRD-to-Solution Assistant",
      description:
        "Transforms Business Requirement Documents into structured, step-by-step AI prompts that guide developers and vibe coders from requirements to implementation faster and with clarity.",
      image: thinkcraftImg,
      link: "https://thinkcraft.comsensetechnologies.com/",
      features: [
        "BRD upload and parsing",
        "AI-generated step-by-step development prompts",
        "Requirement clarity and gap identification",
        "Developer and vibe-coder friendly workflows",
        "Supports faster prototyping and solution design",
      ],
    },
    {
      icon: Network,
      name: "Custom Middleware Platform",
      tagline: "Integration & Orchestration Layer",
      description:
        "A flexible middleware solution that securely connects systems, APIs, and third-party services, enabling reliable data flow, integration, and system modernization.",
      image: middlewareImg,
      link: "/custom-middleware-platform",
      features: [
        "Secure API orchestration",
        "System-to-system integration",
        "Data transformation and routing",
        "Fault-tolerant and scalable design",
        "Centralized monitoring and logging",
      ],
    },
    {
      icon: Smartphone,
      name: "Flutter SDK",
      tagline: "Mobile Development & Connectivity Accelerator",
      description:
        "A production-ready Flutter SDK that accelerates cross-platform mobile development while enabling real-time connectivity, session management, and personalization across mobile and enterprise platforms such as Salesforce MCP.",
      image: flutterImg,
      link: "/flutter-sdk",
      features: [
        "Reusable Flutter components & architecture patterns",
        "Pre-built integrations with enterprise platforms",
        "Real-time event & data synchronization",
        "Unified session management across app and web",
        "Faster iOS & Android development with production-ready SDK",
      ],
    },
    {
      icon: Brain,
      name: "AI Co-Pilot",
      tagline: "Enterprise AI Assistant Platform",
      description:
        "Unified platform to connect org data, configure assistants, and deliver secure analytics.",
      image: aiCopilotImg,
      link: "https://aicopilot.comsensetechnologies.com/",
      features: [
        "Multi-tenant org and user management with granular roles",
        "Secure auth with email, OAuth, and invite-based onboarding",
        "Data connectors, transformation pipelines, and cataloging for AI",
        "Configurable RAG-ready chatbot with multi-model LLM gateway",
        "Natural language analytics, reports, and dashboards",
        "Full audit logs, billing, and admin console for governance",
      ],
    },
    {
      icon: Brain,
      name: "AI Lab",
      tagline: "AI Innovation & Offshore Delivery Center",
      description:
        "Strategic Data & AI partner to build scalable capabilities and accelerate adoption.",
      image: aiLabImg,
      link: "/AI-Lab-ODC",
      features: [
        "Dedicated Data & AI offshore delivery centers with secure, IP-safe teamsDedicated delivery centers",
        "Expertise across Databricks, Snowflake, Azure, and GCP",
        "Proven accelerators for AI product development from prototype to production",
        "End-to-end ML lifecycle with MLOps, automation, and cloud-native architectures",
        "Enterprise AI Academy with structured learning paths and certifications",
        "Track record: 40+ AI initiatives, 20+ products, 10,000+ professionals upskilled",
      ],
    },
    {
      icon: Mail,
      name: "KleanMail",
      tagline: "Real-Time Email Verification Platform",
      description:
        "Fast and accurate email verification for single or bulk emails to improve deliverability.",
      image: kleanmailImg,
      link: "https://www.kleanmail.com/",
      features: [
        "Real-time validation",
        "Bulk list verification",
        "High accuracy and fast response",
        "Reduced bounce rates",
        "Improved email deliverability",
      ],
    },
  ];
  const testimonials = [
    {
      quote: "Strong engineering depth with a clear AI vision",
      role: "Technology Head, Enterprise Manufacturing Company",
      content:
        "The team demonstrated strong engineering fundamentals and helped us implement AI-driven automation without disrupting our existing systems. Their structured approach and reusable accelerators significantly reduced our delivery timelines.",
    },
    {
      quote: "AI agents that actually work in production",
      role: "Chief Digital Officer, BFSI Organization",
      content:
        "What stood out was their ability to move beyond PoCs. The AI agents and predictive models were production-ready, well-governed, and integrated seamlessly into our workflows. We saw measurable productivity gains within weeks.",
    },
    {
      quote: "Reliable partner for complex data and AI programs",
      role: "Head of Analytics, Automotive Company",
      content:
        "They helped us build scalable data platforms, deploy AI models, and continuously optimize performance through managed services. Their ability to combine engineering, data, and AI made a real difference.",
    },
    {
      quote: "Outcome-focused, not just delivery-focused",
      role: "Founder, Digital-First Consumer Brand",
      content:
        "Unlike typical vendors, the team focused on business outcomes. Their accelerators, AI models, and managed support helped us move faster with lower risk.",
    },
  ];
  const [tstIndex, setTstIndex] = React.useState(0);
  const [tstPaused, setTstPaused] = React.useState(false);
  React.useEffect(() => {
    if (tstPaused) return;
    const i = setInterval(
      () => setTstIndex((p) => (p + 1) % testimonials.length),
      5000,
    );
    return () => clearInterval(i);
  }, [tstPaused, testimonials.length]);
  const nextTst = () => setTstIndex((p) => (p + 1) % testimonials.length);
  const prevTst = () =>
    setTstIndex((p) => (p - 1 + testimonials.length) % testimonials.length);
  const whatWeDo = [
    {
      icon: Code2,
      title: "Data & Digital Engineering",
      what: "Build secure, cloud-native data pipelines and develop web apps, portals, APIs, and microservices that extend Salesforce into a connected digital ecosystem.",
      why: "Enables reliable, scalable data flow while creating seamless digital experiences that enhance agility and support informed decision-making.",
    },
    {
      icon: Zap,
      title: "Accelerators & Industry-Ready Solutions",
      what: "Offer pre-built connectors, reusable SDKs, data models, identity frameworks, AI pipelines, and Salesforce solutions tailored for Automotive, BFSI, Retail, Real Estate, and Manufacturing.",
      why: "Speeds up integrations, reduces delivery time, lowers risk, and accelerates deployment of industry-specific capabilities, driving faster time-to-value.",
    },
    {
      icon: Cloud,
      title: "Cloud, Security & Compliance",
      what: "Architect cloud solutions with data security, access control, and governance.",
      why: "Protects critical enterprise data while ensuring regulatory compliance.",
    },
    {
      icon: Shield,
      title: "Comprehensive Security & Compliance",
      what: "Implement end-to-end security across data, applications, platforms, and cloud environments—including data encryption, key management, tokenization, secure coding, vulnerability assessments, penetration testing, runtime protection, and compliant cloud architectures.",
      why: "Protects sensitive and regulated data, reduces attack surfaces, ensures applications and platforms are resilient, and delivers secure, compliant, enterprise-ready cloud environments.",
    },
    {
      icon: Brain,
      title: "AI Agents, Models & Intelligent Automation",
      what: "Build autonomous AI agents, deploy pre-built and custom AI/ML models, and deliver generative AI solutions such as chatbots, document intelligence, internal copilots, and workflow automation.",
      why: "Automates complex and routine tasks, improves prediction and personalization, boosts team productivity, and enables faster, data-driven decision-making.",
    },
    {
      icon: Network,
      title: "Migration & Modernization",
      what: "Migrate legacy systems and data to Salesforce and cloud platforms.",
      why: "Modernizes infrastructure with minimal risk, downtime, or business disruption.",
    },
  ];
  return (
    <>
      <Helmet>
        <title>
          Engineering & Product Development Company Pune | AI & Salesforce
        </title>

        <meta
          name="description"
          content="Build scalable apps, APIs & data pipelines with AI-driven engineering services. Salesforce integration & cloud solutions in Pune, India."
        />

        <meta
          name="keywords"
          content="product engineering India, Salesforce integration Pune, AI software development India"
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Comsense Technologies" />

        {/* Open Graph */}
        <meta
          property="og:title"
          content="Engineering & Product Development Company Pune | AI & Salesforce"
        />
        <meta
          property="og:description"
          content="AI-driven product engineering, APIs & Salesforce integration services in Pune."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://www.comsensetechnologies.com/engineering"
        />
      </Helmet>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@100..900&display=swap');
        *{ font-family: "Outfit", sans-serif !important;}
        .cmp-hero-section { position: relative; padding: 70px 0; background: #fff; overflow: hidden; }
        .cmp-grid { position:absolute; inset:0; background-image: radial-gradient(#eaeef5 1px, transparent 1px); background-size: 24px 24px; opacity:.4; pointer-events:none; z-index:0; }
        .cmp-badge { display:inline-flex; align-items:center; gap:8px; padding:8px 16px; background:#fff; border:1px solid #e2e8f0; border-radius:999px; box-shadow:0 2px 10px rgba(0,0,0,0.05); font-size:12px; font-weight:700; color:#0f172a; text-transform:uppercase; letter-spacing:1px; }
                .cmp-badge-2 { display:inline-flex; align-items:center; gap:8px; padding:8px 16px; background:#fff; border:1px solid #e2e8f0; border-radius:999px; box-shadow:0 2px 10px rgba(0,0,0,0.05); font-size:12px; font-weight:700; color:#0f172a; text-transform:uppercase; letter-spacing:0px !important; }

        .cmp-badge-dot { width:6px; height:6px; background:#ee0000; border-radius:50%; animation: cmpPulseDot 2s ease-in-out infinite; }
        @keyframes cmpPulseDot { 0%,100%{ transform: scale(1); } 50%{ transform: scale(1.3); } }
        .cmp-title { font-size:42px; font-weight:800; text-align:left; color:#0f172a; letter-spacing:-.5px; margin: 12px 0; }
        .cmp-highlight { color:#ee0000; }
        .cmp-underline { height:4px; background:#ee0000; border-radius:2px; margin:12px 0 0; width:0; }
        .cmp-sub { font-size:18px; color:#475569; text-align:left; max-width:900px; margin: 14px 0 0; }
        .cmp-buttons { display:flex; gap:12px; justify-content:flex-start; margin-top: 22px; flex-wrap:wrap; }
        .btn-cmp-red, .btn-cmp-white { display:inline-flex; align-items:center; gap:8px; padding:13px 22px; border-radius:999px; font-weight:800; transition: transform .2s ease, box-shadow .2s ease; }
        .btn-cmp-red { background:#ee0000; color:#fff; border:none; box-shadow:0 20px 40px -20px rgba(238,0,0,0.55); }
        .btn-cmp-white { background:#fff; color:#ee0000; border:2px solid #ee0000; box-shadow:0 12px 24px -12px rgba(238,0,0,0.25); }
        .btn-cmp-red:hover, .btn-cmp-white:hover { transform: translateY(-2px) scale(1.05); }
        .btn-cmp-red:active, .btn-cmp-white:active { transform: scale(.98); }
        .btn-cmp-red svg, .btn-cmp-white svg { transition: transform .2s ease; }
        .btn-cmp-red:hover svg, .btn-cmp-white:hover svg { transform: translateX(4px); }
        .cmp-desc-box { max-width: 960px; margin: 30px 0 0; background: rgba(255,255,255,0.87); border:1px solid #ffd4d4; border-radius:16px; padding:18px 22px; backdrop-filter: blur(8px); color: #374151; }
        @media (max-width: 992px) { .cmp-title { font-size: 42px; } }
        @media (max-width: 768px) { .cmp-title { font-size: 34px; } .cmp-sub { font-size: 16px; } }

        .cmp-overview-section { position: relative; padding: 80px 0; background: #effdff; overflow: hidden; }
        .cmp-overview-grid { position:absolute; inset:0; background-image: radial-gradient(#eaeef5 1px, transparent 1px); background-size: 24px 24px; opacity:.35; pointer-events:none; z-index:0; }
        .cmp-overview-title { font-size:42px; font-weight:800; text-align:center; color:#0f172a; letter-spacing:-.5px; }
        .cmp-overview-underline { height:4px; background:#ee0000; border-radius:2px; margin:12px auto 0; width:0; }
        .cmp-overview-text { font-size:18px; color:#475569; text-align:center; max-width:1000px; margin:14px auto 30px auto; line-height:30px; }

        .highlight-card { position:relative; background:#fff; border-radius:24px;  box-shadow: 0 8px 18px rgba(0,0,0,0.06); padding:22px; transition: transform .3s ease, box-shadow .3s ease; }
        .highlight-card:hover { transform: translateY(-8px) scale(1.02); box-shadow: 0 20px 40px rgba(238,0,0,0.15); }
        .highlight-row { margin-top: 18px; }
        .highlight-item { display:flex; align-items:center; gap:14px; }
        .highlight-icon { width:36px; height:36px; border-radius:50%; display:flex; align-items:center; justify-content:center; background:#fff5f5; color:#ee0000; border:1px solid #ffd4d4; font-size:0; }
        .highlight-text { width:calc(100% - 46px); font-size:16px; color:#111827; font-weight:700; }

        .approach-card { position:relative; background:#fff; border-radius:24px; border:1px solid #cfe6ff; box-shadow:0 8px 18px rgba(0,0,0,0.06); padding:22px; transition: transform .3s ease, box-shadow .3s ease; }
        .approach-card:hover { transform: translateY(-6px) scale(1.02); box-shadow: 0 22px 40px rgba(238,0,0,0.12); border-color:#ee0000; }
        .approach-title { font-size:22px; font-weight:800; color:#111827; line-height:1.3; margin-top: 10px; }
        .approach-text { font-size:15px; color:#475569; line-height:26px; margin-top: 8px; }

        .cmp-excellence-section { position: relative; padding: 80px 0; background: #fff; overflow: hidden; }
        .ex-card { position:relative; background:#fff; border-radius:22px; box-shadow: 0 8px 18px rgba(0,0,0,0.06); padding:24px; transition: transform .3s ease, box-shadow .3s ease; border:1px solid #e5e7eb; }
        .ex-card:hover { transform: translateY(-6px); box-shadow: 0 22px 40px rgba(238,0,0,0.12); border-color:#ee0000; }
        .ex-icon { width:54px; height:54px; border-radius:12px; display:flex; align-items:center; justify-content:center; background:#ee0000; color:#fff; margin-bottom: 14px; box-shadow:0 12px 24px -12px rgba(238,0,0,0.35); }
        .ex-title { font-size:20px; font-weight:800; color:#111827; }
        .ex-text { font-size:15px; color:#475569; line-height:26px; }

        .ex-heading-title { font-size:42px; font-weight:800; text-align:center; color:#0f172a; letter-spacing:-.5px; }
        .ex-heading-sub { font-size:18px; color:#475569; text-align:center; max-width:1000px; margin: 10px auto 30px auto; line-height:30px; }
.feature-card:hover{
  transform: translateY(-6px) scale(1.02);
  box-shadow: 0 22px 40px rgba(238,0,0,0.12);
  border-color: #ee0000;}

        .feature-card { transition: transform .3s ease, box-shadow .3s ease;position:relative; background:#fff; border-radius:24px; box-shadow:0 10px 22px rgba(0,0,0,0.06); padding:28px; border:1px solid #e5e7eb; }
        .feature-title { font-size:22px; font-weight:800; color:#0f172a; font-weight: 800;
  color: #111827;
  line-height: 1.3;
  margin-top: 10px;}
        .feature-sub { font-size:15px; color:#475569; margin-top:6px; }
        .chip-marquee { overflow:hidden; width:100%; margin-top:16px; }
        .chip-row { display:inline-flex; gap:10px; flex-wrap:nowrap; align-items:center; min-width:max-content; animation: cmpMarquee 80s linear infinite; }
        .chip-row:hover { animation-play-state: paused; }
        @keyframes cmpMarquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .chip { display:inline-flex; align-items:center; padding:3px 10px; border-radius:999px; background:#f1f5f9; color:#0f172a; font-weight:700; border:1px solid #e2e8f0; font-size:10px; }
        .chip-active { background:#e6f1ff; color:#0f172a; border-color:#bfdbfe; }
        .metrics-row { display:flex; gap:12px; flex-wrap:wrap; margin-top:18px; }
        .metric-badge { display:inline-flex; align-items:center; gap:8px; padding:5px 10px; font-size:10px; border-radius:999px; font-weight:700; }
        .metric-badge svg { width:15px; height:15px; }
        .metric-red { background:#fff5f5; color:#ee0000; border:1px solid #ffd4d4; }
        .metric-blue { background:#e6f1ff; color:#0f172a; border:1px solid #bfdbfe; }
        .feature-media { position:absolute; right:20px; bottom:10px; width:160px; opacity:.12; pointer-events:none; }
        .feat-icon { width:44px; height:44px; border-radius:12px; display:flex; align-items:center; justify-content:center; background:#ee0000; color:#fff; margin-bottom:12px; }

        .cmp-how-section { position: relative; padding: 80px 0; background: #e2f1fe; overflow: hidden; }
        .how-diagram { display:flex; align-items:center; justify-content:center; gap:18px; flex-wrap:wrap; }
        .how-box { background:#f1f5f9; color:#0f172a; border-radius:999px; padding:10px 18px; font-weight:800; }
        .how-box-mid { background:#ee0000; color:#fff; border-radius:999px; padding:10px 18px; font-weight:800; }
        .how-arrow { color:#ee0000; font-weight:800; }
        .how-bullet { display:flex; align-items:flex-start; gap:12px; }
        .how-bullet-icon { width:28px; height:28px; border-radius:50%; display:flex; align-items:center; justify-content:center; background:#fff5f5; color:#ee0000; border:1px solid #ffd4d4; flex-shrink:0; }
        .how-bullet-text { color:#475569; font-size:16px; line-height:26px; }
.hero-tag {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #ffefef;
  color: #ee0000;
  border: 1px solid #ffd4d4;
  font-weight: 700;
  font-size: 12px;
}
        .cmp-services-section { position: relative; padding: 80px 0; background: #fff; overflow: hidden; }
        .cmp-solutions-section { position: relative; padding: 80px 0; background: #fff; }
        .sol-title { font-size:42px; font-weight:800; text-align:center; color:#0f172a; letter-spacing:-.5px; }
        .sol-sub { font-size:18px; color:#475569; text-align:center; max-width:1000px; margin:10px auto 30px auto; line-height:30px; }
        .sol-card { background:#fff; border-radius:24px; box-shadow:0 10px 22px rgba(0,0,0,0.08); border:2px solid #cfe6ff; padding:24px; transition: transform .3s ease, box-shadow .3s ease, border-color .3s ease; }
        .sol-card:hover { transform: translateY(-6px); box-shadow:0 24px 48px rgba(0,0,0,0.12); border-color:#80bfff; }
        .sol-badge { display:inline-flex; align-items:center; gap:10px; background:#ffefef; border:1px solid #ffd4d4; border-radius:999px; padding:6px 10px; color:#991b1b; font-weight:800; font-size:12px; }
        .sol-icon { width:28px; height:28px; border-radius:50%; display:flex; align-items:center; justify-content:center; background:#ee0000; color:#fff; }
        .sol-media { width:100%; height:100%; object-fit:contain; }
        .sol-title-card { font-size:28px; font-weight:800; color:#0f172a; margin:10px 0; }
        .sol-desc { font-size:16px; color:#475569; line-height:26px; }
        .sol-features { background:#f8fafc; border-left:4px solid #ee0000; border-radius:16px; padding:16px; margin-top:12px; }
        .sol-features-title { font-size:12px; text-transform:uppercase; color:#ee0000; font-weight:800; letter-spacing:.5px; margin-bottom:8px; }
        .sol-feature { display:flex; align-items:flex-start; gap:8px; font-size:14px; color:#475569; }
        .sol-dot { width:6px; height:6px; border-radius:50%; background:#ee0000; margin-top:7px; flex-shrink:0; }
        .sol-cta { display:inline-flex; align-items:center; gap:8px; color:#ee0000; font-weight:800; margin-top:10px; text-decoration:none; }
        .sol-cta:hover { gap:10px; }
        .cmp-testimonials-section { position:relative; padding:80px 0; background:#e2f1fe; }
        .tst-title { font-size:42px; font-weight:800; text-align:center; color:#0f172a; letter-spacing:-.5px; }
        .tst-sub { font-size:18px; color:#475569; text-align:center; max-width:900px; margin:10px auto 30px auto; }
        .tst-card { border-radius:24px; overflow:hidden; box-shadow:0 12px 24px rgba(0,0,0,0.08); border:2px solid #e5e7eb; display:grid; grid-template-columns:1fr 1fr; }
        @media (max-width: 768px) { .tst-card { grid-template-columns:1fr; } }
        .tst-left { background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%); padding:26px; position:relative; }
        .tst-right { background: linear-gradient(135deg, #ee0000 0%, #ff4d4d 100%); padding:26px; color:#fff; position:relative; padding-bottom:72px;background: linear-gradient(135deg, #067bb1 0%, #04467a 100%);
  border: 1px solid #067bb1; }
        .tst-quote-bg { position:absolute; right:16px; top:16px; opacity:.08; }
        .tst-quote { font-size:24px; font-weight:800; color:#0f172a; }
        .tst-role { font-size:14px; color:#475569; font-weight:700; margin-top:6px; }
        .tst-content { font-size:15px; color:#0f172a; line-height:26px; margin-top:12px; }
        .tst-content-white { font-size:15px; color:#ffffff; line-height:26px; margin-top:6px; opacity:.95; }
        .tst-bottombar { position:absolute; left:20px; right:20px; bottom:16px; display:flex; align-items:center; justify-content:space-between; }
        .tst-controls { display:flex; align-items:center; gap:10px; }
        .tst-dot { width:8px; height:8px; border-radius:999px; background: rgba(255,255,255,0.7); box-shadow: 0 2px 6px rgba(0,0,0,0.08); }
        .tst-dot-active { width:28px; height:8px; border-radius:999px; background: linear-gradient(90deg, #22c3ff, #3b82f6); box-shadow: 0 4px 10px rgba(34,195,255,0.35); }
        .tst-nav { display:flex; gap:10px; align-items:center; }
        .tst-btn { background:#fff; border:2px solid #fff; border-radius:16px; width:42px; height:42px; display:flex; align-items:center; justify-content:center; color:#475569; box-shadow: 0 8px 16px rgba(0,0,0,0.08); }
        .tst-btn:hover { border-color:#e5e7eb; background:#f8fafc; color:#0f172a; box-shadow: 0 10px 20px rgba(0,0,0,0.10); }
        .tst-role-right { display:inline-flex; align-items:center; 
        // gap:10px; 
        padding:10px 0px; border-radius:999px; 
        // background: rgba(255,255,255,0.12); 
        // border:1px solid rgba(255,255,255,0.25); 
        margin-top:20px;
        color:#fff; font-weight:800; letter-spacing:.2px; }
        // .tst-role-dot { width:8px; height:8px; border-radius:50%; background:#fff; opacity:.9; }
        .cmp-what-section { position:relative; padding:80px 0; background:#e2f1fe; }
        .what-title { font-size:42px; font-weight:800; text-align:center; color:#0f172a; letter-spacing:-.5px; }
        .what-sub { font-size:18px; color:#475569; text-align:center; max-width:1000px; margin:10px auto 30px auto; line-height:30px; }
        .what-card { position:relative; background:#fff; border-radius:22px; box-shadow:0 8px 18px rgba(0,0,0,0.06); padding:22px; border:1px solid #e5e7eb; transition: transform .3s ease, box-shadow .3s ease, border-color .3s ease; }
        .what-card:hover { transform: translateY(-5px); box-shadow:0 20px 40px rgba(238,0,0,0.15); border-color:#ee0000; }
        .what-icon { width:48px; height:48px; border-radius:12px; display:flex; align-items:center; justify-content:center; background:#ee0000; color:#fff; margin-bottom:12px; }
        .what-card-title { font-size:20px; font-weight:800; color:#111827; margin-bottom:12px; }
        .what-block { margin-top:10px; }
        .what-badge { position:relative; display:inline-block; font-size:12px; font-weight:800; text-transform:uppercase; color:#0f172a; padding-left:12px; letter-spacing:0.2px; }
        .what-badge-blue::before { content:''; position:absolute; left:0; top:50%; transform:translateY(-50%); width:3px; height:16px; background:#22c3ff; border-radius:2px; }
        .what-badge-red::before { content:''; position:absolute; left:0; top:50%; transform:translateY(-50%); width:3px; height:16px; background:#ee0000; border-radius:2px; }
        .what-text { font-size:15px; color:#475569; line-height:26px; margin-top:6px; }
        .service-card { position:relative; background:#fff; border-radius:22px; box-shadow: 0 8px 18px rgba(0,0,0,0.06); padding:22px; transition: transform .3s ease, box-shadow .3s ease, border-color .3s ease; border:1px solid #e5e7eb; }
        .service-card:hover { transform: translateY(-5px); box-shadow: 0 20px 40px rgba(238,0,0,0.15); border-color:#ee0000; }
        .service-icon { width:48px; height:48px; border-radius:12px; display:flex; align-items:center; justify-content:center; background:#ee0000; color:#fff; }
        .service-title { font-size:18px; font-weight:800; color:#111827; margin:4px 0 10px; }
        .service-text { font-size:15px; color:#475569; line-height:26px; }

        .cmp-cta-section { position: relative; padding: 110px 0; background: #effdff; overflow: hidden; }
        .cta-orb { position:absolute; border-radius:50%; filter: blur(100px); z-index:0; }
        .cta-orb-1 { top:-40px; left:-40px; width:500px; height:500px; background: rgba(238,0,0,0.10); }
        .cta-orb-2 { bottom:-60px; right:-60px; width:600px; height:600px; background: rgba(238,0,0,0.05); filter: blur(120px); }
        .cmp-cta-title { font-size:42px; font-weight:800; text-align:center; color:#0f172a; letter-spacing:-.5px; }
        .cmp-cta-text { font-size:18px; color:#475569; text-align:center; max-width:920px; margin: 10px auto 0; }
      `}</style>

      <section className="cmp-hero-section">
        <div className="cmp-grid"></div>
        <Container>
          <Row
            className="align-items-center justify-content-between position-relative"
            style={{ zIndex: 2 }}
          >
            <Col lg="7">
              <motion.div>
                <div className="cmp-badges hero-tag">
                  ⚡ {/* <span className="cmp-badge-dot"></span> */} Engineering
                </div>
                <h1 className="cmp-title">
                  Built with <span className="cmp-highlight">clarity</span>.
                  Scaled with <span className="cmp-highlight">purpose</span>.
                  Enhanced by <span className="cmp-highlight">AI</span>.
                </h1>
                {/* <div className="cmp-underline" style={{ width: 160 }}></div> */}
                <p className="cmp-sub">
                  We deliver software and solutions that are precise, scalable,
                  and enhanced with applied intelligence.
                </p>
                <div className="cmp-buttons">
                  <Link
                    to="/contact"
                    style={{ textDecoration: "none" }}
                    className="btn-cmp-red"
                  >
                    Get Started <ArrowRight size={18} />
                  </Link>
                  <Link
                    to="/contact"
                    style={{ textDecoration: "none" }}
                    className="btn-cmp-white"
                  >
                    Explore Solutions
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </motion.div>
            </Col>
            <Col lg="5" className="text-center">
              <div className="hero-media m-auto" style={{ width: "90%" }}>
                <Image src={flutterHero} alt="GCC hero" className="w-100" />
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="cmp-overview-section">
        <div className="cmp-overview-grid"></div>
        <Container>
          <Row>
            <Col lg={12}>
              <h2 className="cmp-overview-title">Our Approach</h2>
              <p className="cmp-overview-text">
                Precision, scalability, and intelligence in everything we build
              </p>
            </Col>
          </Row>
          <Row className="highlight-row">
            {approachCards.map((item) => (
              <Col key={item.badge} lg={3} md={6} className="mb-4">
                <div className="approach-card h-100">
                  <div className="cmp-badge cmp-badge-2">
                    <span className="cmp-badge-dot"></span> {item.badge}
                  </div>
                  <div className="approach-title">{item.title}</div>
                  <p className="approach-text">{item.text}</p>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="cmp-excellence-section">
        <Container>
          <Row className="mb-3">
            <Col lg={12} className="text-center">
              <h2 className="ex-heading-title">Areas of Excellence</h2>
              <p className="ex-heading-sub">
                Comprehensive engineering capabilities to transform your digital
                vision into reality
              </p>
            </Col>
          </Row>

          <Row>
            {excellenceFeatures.map((f, idx) => (
              <Col key={f.title} lg={3} md={6} className="mb-4">
                <div className="feature-card h-100">
                  <div className="feat-icon">
                    <f.icon size={20} />
                  </div>
                  <div className="feature-title">{f.title}</div>
                  <p className="feature-sub">{f.sub}</p>
                  {f.chips && (
                    <div className="chip-marquee">
                      <div
                        className="chip-row"
                        style={{ animationDuration: `${f.chips.length * 4}s` }}
                      >
                        {[...f.chips, ...f.chips].map((c, ci) => (
                          <span
                            key={`${c}-${ci}`}
                            className={`chip ${ci % f.chips.length === 1 ? "chip-active" : ""}`}
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {f.metrics && (
                    <div className="metrics-row">
                      <div className="metric-badge metric-red">
                        <CircleDot size={18} /> {f.metrics[0]}
                      </div>
                      <div className="metric-badge metric-blue">
                        <Star size={18} /> {f.metrics[1]}
                      </div>
                    </div>
                  )}
                  {idx === 0 && (
                    <Image src={LayerTwo} className="feature-media" />
                  )}
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      <section className="cmp-what-section">
        <Container>
          <Row className="mb-3">
            <Col lg={12} className="text-center">
              <h2 className="what-title">What We Do, Why It Matters</h2>
              <p className="what-sub">
                Delivering impactful solutions that drive real business outcomes
              </p>
            </Col>
          </Row>
          <Row>
            {whatWeDo.map((w) => (
              <Col key={w.title} lg={6} md={12} className="mb-4">
                <div className="what-card h-100">
                  <div className="what-icon">
                    <w.icon size={20} />
                  </div>
                  <div className="what-card-title">{w.title}</div>
                  <div className="what-block">
                    <div className="what-badge what-badge-blue">What we do</div>
                    <p className="what-text">{w.what}</p>
                  </div>
                  <div className="what-block">
                    <div className="what-badge what-badge-red">
                      Why it matters
                    </div>
                    <p className="what-text">{w.why}</p>
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* <section className="cmp-how-section">
                <Container>
                    <Row>
                        <Col lg={12} className="text-center">
                            <h2 className="cmp-overview-title">How We Ship</h2>
                            <div className="cmp-overview-underline" style={{ width: 110 }}></div>
                            <div className="how-diagram mt-3">
                                <span className="how-box">Requirements</span>
                                <span className="how-arrow">→</span>
                                <span className="how-box">Architecture</span>
                                <span className="how-arrow">→</span>
                                <span className="how-box-mid">Build & Validate</span>
                                <span className="how-arrow">→</span>
                                <span className="how-box">Release</span>
                                <span className="how-arrow">→</span>
                                <span className="how-box">Scale</span>
                            </div>
                        </Col>
                    </Row>
                    <Row className="mt-4">
                        {[
                            { icon: Shield, title: 'Security by Design', text: 'Policies, auth, and encryption embedded in the lifecycle.' },
                            { icon: Activity, title: 'Observability', text: 'Metrics, tracing, and alerting from day one.' },
                            { icon: BarChart3, title: 'Measurable Outcomes', text: 'Define and track impact per release.' }
                        ].map((item, i) => (
                            <Col key={item.title} lg={4} md={6} className="mb-3">
                                <div className="how-card h-100 d-flex align-items-start gap-3">
                                    <div className="how-icon-box"><item.icon size={20} /></div>
                                    <div>
                                        <div className="how-card-title">{item.title}</div>
                                        <p className="how-card-text">{item.text}</p>
                                    </div>
                                </div>
                            </Col>
                        ))}
                    </Row>
                </Container>
            </section> */}

      <section className="cmp-solutions-section">
        <Container>
          <Row className="mb-3">
            <Col lg={12} className="text-center">
              <h2 className="sol-title">Our Solutions</h2>
              <p className="sol-sub">
                Purpose-built products and platforms to accelerate your digital
                transformation
              </p>
            </Col>
          </Row>
          {solutions.map((s, i) => (
            <Row key={s.name} className="mb-4 align-items-stretch">
              <Col lg={6} className={i % 2 === 1 ? "order-lg-2" : ""}>
                <div className="sol-card h-100">
                  <div className="sol-badge">
                    <div className="sol-icon">
                      <s.icon size={14} />
                    </div>
                    {s.tagline}
                  </div>
                  {s.link ? (
                    s.link.startsWith("/") ? (
                      <Link
                        to={s.link}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <div className="sol-title-card">{s.name}</div>
                      </Link>
                    ) : (
                      <a
                        href={s.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <div className="sol-title-card">{s.name}</div>
                      </a>
                    )
                  ) : (
                    <div className="sol-title-card">{s.name}</div>
                  )}
                  <p className="sol-desc">{s.description}</p>
                  <div className="sol-features">
                    <div className="sol-features-title">Key Features</div>
                    {s.features.map((f) => (
                      <div key={f} className="sol-feature">
                        <div className="sol-dot"></div>
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                  {s.link &&
                    (s.link.startsWith("/") ? (
                      <Link to={s.link} className="sol-cta">
                        Explore Solution <ArrowRight size={18} />
                      </Link>
                    ) : (
                      <a
                        href={s.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="sol-cta"
                      >
                        Explore Solution <ArrowRight size={18} />
                      </a>
                    ))}
                </div>
              </Col>
              <Col lg={6} className={i % 2 === 1 ? "order-lg-1" : ""}>
                <div
                  className="sol-card h-100 d-flex align-items-center justify-content-center"
                  style={{ padding: 20 }}
                >
                  {s.link ? (
                    s.link.startsWith("/") ? (
                      <Link
                        to={s.link}
                        style={{
                          display: "block",
                          width: "100%",
                          textAlign: "center",
                        }}
                      >
                        <Image
                          src={s.image}
                          alt={s.name}
                          className="sol-media"
                        />
                      </Link>
                    ) : (
                      <a
                        href={s.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: "block",
                          width: "100%",
                          textAlign: "center",
                        }}
                      >
                        <Image
                          src={s.image}
                          alt={s.name}
                          className="sol-media"
                        />
                      </a>
                    )
                  ) : (
                    <Image src={s.image} alt={s.name} className="sol-media" />
                  )}
                </div>
              </Col>
            </Row>
          ))}
        </Container>
      </section>

      <section className="cmp-testimonials-section">
        <Container>
          <Row className="mb-3">
            <Col lg={12} className="text-center">
              <h2 className="tst-title">Hear from Our Customers</h2>
              <p className="tst-sub">
                Real impact and outcomes from engineering-led delivery
              </p>
            </Col>
          </Row>
          <Row>
            <Col lg={12}>
              <div
                className="tst-card"
                onMouseEnter={() => setTstPaused(true)}
                onMouseLeave={() => setTstPaused(false)}
              >
                <div className="tst-left">
                  <div className="tst-quote-bg">
                    <Quote size={96} />
                  </div>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`left-${tstIndex}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.45, ease: "easeOut" }}
                    >
                      <div className="tst-quote">
                        {testimonials[tstIndex].quote}
                      </div>
                      <p className="tst-content">
                        {testimonials[tstIndex].content}
                      </p>
                    </motion.div>
                  </AnimatePresence>
                </div>
                <div className="tst-right">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <div className="cmp-badge">
                      <span className="cmp-badge-dot"></span> Customer Story
                    </div>
                  </div>
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={`role-${tstIndex}`}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      transition={{ duration: 0.45, ease: "easeOut" }}
                    >
                      <div className="tst-role-right">
                        <span className="tst-role-dot"></span>
                        {testimonials[tstIndex].role}
                      </div>
                    </motion.div>
                  </AnimatePresence>
                  <div className="tst-bottombar">
                    <div className="tst-controls">
                      {testimonials.map((_, i) => (
                        <div
                          key={i}
                          className={`tst-dot ${i === tstIndex ? "tst-dot-active" : ""}`}
                        ></div>
                      ))}
                    </div>
                    <div className="tst-nav">
                      <button
                        className="tst-btn"
                        aria-label="Prev"
                        onClick={prevTst}
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <button
                        className="tst-btn"
                        aria-label="Next"
                        onClick={nextTst}
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default EngineeringServices;
